/**
 * useCartBootstrap — orchestration hook for the CartBootstrapStep wizard.
 *
 * Given the four bootstrap inputs (officer / branch / entityType / intent),
 * this hook:
 *   1. Builds a `CreateCartPayload` per SOLUTION-BIBLE §RAO.
 *   2. Calls `CartProvider.createCart()` which POSTs /v3/carts.
 *   3. On success, navigates to `/rao/{cartId}` and lets CartDetail take over.
 *   4. Surfaces any failure as a string for the wizard's error banner.
 *
 * The hook is intentionally thin — all server state stays in CartContext so
 * the newly-created cart is immediately available to every downstream step.
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import type { CreateCartPayload, Origin } from '@retail-ops/shared-types';
import { useCart } from '@/contexts/CartContext';

/**
 * Entity types captured in step 1. Values are canonical `CustomerType` enum
 * strings so downstream `entityRules` lookups match. The officer-facing UI
 * renders friendly labels while storing the canonical value (plus `isJoint`
 * sidecar metadata for the "Joint Individual" variant).
 */
export type CartBootstrapEntityType =
  | 'Individual'
  | 'Corporation'
  | 'Trust'
  | 'Estate'
  | 'Conservatorship'
  | 'NonProfit'
  | 'MinorAccount';

/**
 * Cart creation intent — drives downstream routing in later steps
 * (existing-customer dedupe, lobby handoff, etc).
 */
export type CartBootstrapIntent =
  | 'open-new'
  | 'add-to-existing'
  | 'convert-from-lobby';

export interface CartBootstrapInputs {
  officerCode: string;
  branchId: string;
  intent: CartBootstrapIntent;
  /**
   * Optional — entity category is no longer collected on the bootstrap step
   * (per Bill's 2026-04-24 directive; it's now picked per-person inside
   * PeopleStep via EntityCategoryPicker). This field is kept as optional for
   * backward compat with any caller that still supplies it; downstream
   * persistence records null when omitted.
   */
  entityType?: CartBootstrapEntityType;
  /** When true, seed `ownership.acctOwnership='Joint'` (UI variant of Individual). */
  isJoint?: boolean;
  /** Optional — when intent='add-to-existing' carry the target CIF. */
  existingCifId?: string;
  /** Optional — when intent='convert-from-lobby' carry the source session. */
  lobbySessionId?: string;
  /** Origin tag passed through to CartDto.origin. */
  origin?: Origin;
}

export interface UseCartBootstrapResult {
  submitting: boolean;
  error: string | null;
  /** Kick the POST /v3/carts flow. Returns cartId on success, null on fail. */
  bootstrap: (inputs: CartBootstrapInputs) => Promise<string | null>;
}

/**
 * Build the CartDto metadata envelope from the bootstrap inputs. The
 * Function's cart-create handler reads this to seed entity rules and
 * downstream task generation.
 */
function buildMetadata(inputs: CartBootstrapInputs): Record<string, unknown> {
  const isJoint = !!inputs.isJoint;
  // Entity type is no longer collected on bootstrap — PeopleStep sets it per
  // person. Persist null so downstream readers distinguish "not yet chosen"
  // from a stale legacy value.
  const entityType = inputs.entityType ?? null;
  const out: Record<string, unknown> = {
    bootstrap: {
      entityType,
      intent: inputs.intent,
      isJoint,
      capturedAt: new Date().toISOString(),
    },
    // Pre-seed OwnershipStep's read path so the radio is selected on arrival.
    ownership: {
      entityType,
      ...(isJoint ? { acctOwnership: 'Joint' } : {}),
    },
  };
  if (inputs.existingCifId) {
    out['existingCifId'] = inputs.existingCifId;
  }
  if (inputs.lobbySessionId) {
    out['lobbySessionId'] = inputs.lobbySessionId;
  }
  return out;
}

export function useCartBootstrap(): UseCartBootstrapResult {
  const navigate = useNavigate();
  const { createCart } = useCart();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bootstrap = useCallback(
    async (inputs: CartBootstrapInputs): Promise<string | null> => {
      setSubmitting(true);
      setError(null);

      // Basic client-side guard — the Function also validates but we fail
      // loud in-wizard so the officer never gets a cryptic 400.
      if (!inputs.officerCode.trim()) {
        setSubmitting(false);
        setError('Officer code is required.');
        return null;
      }
      if (!inputs.branchId.trim()) {
        setSubmitting(false);
        setError('Branch is required.');
        return null;
      }

      // Use `cartDataJson` (not `metadataJson`) — the Function's
      // CartCreateInput reads CartDataJson and routes it to the SQL
      // `MetadataJson` column. Sending `metadataJson` gets silently dropped.
      // Smoke-test v0.1.28 caught the bootstrap envelope losing entityType +
      // intent; this keeps them on the created cart.
      const payload: CreateCartPayload = {
        origin: inputs.origin ?? 'fsr-inbranch',
        officerCode: inputs.officerCode.trim(),
        branch: inputs.branchId.trim(),
        cartDataJson: JSON.stringify(buildMetadata(inputs)),
      };

      const cart = await createCart(payload);
      setSubmitting(false);

      if (!cart) {
        setError('Unable to create cart. Check network and try again.');
        return null;
      }

      navigate(`/rao/${cart.cartId}`);
      return cart.cartId;
    },
    [createCart, navigate],
  );

  return { submitting, error, bootstrap };
}
